import { apiClient } from '@/lib/http/client'
import {
  PrivacySubject,
  UpsertPrivacyRequest,
  UpsertPrivacyResponse,
  UpdatePrivacyRequest,
  GetPrivacyResponse,
  ListPrivacyResponse,
  ListPrivacyParams,
  DeletePrivacyResponse,
  ImportPrivacyResponse,
  PrivacyStatsResponse,
  ListAuditResponse,
} from '@/models/privacy'

class PrivacyService {
  private basePath = '/v1/privacy'

  /**
   * List privacy subjects with pagination
   */
  async list(params?: ListPrivacyParams): Promise<ListPrivacyResponse> {
    return apiClient.get<ListPrivacyResponse>(this.basePath, {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 50,
    })
  }

  /**
   * Get a single privacy subject by ID
   */
  async get(id: string): Promise<PrivacySubject> {
    const response = await apiClient.get<GetPrivacyResponse>(`${this.basePath}/${id}`)
    return response.subject
  }

  /**
   * Create or update a privacy subject (upsert by email)
   */
  async upsert(request: UpsertPrivacyRequest): Promise<UpsertPrivacyResponse> {
    return apiClient.post<UpsertPrivacyResponse>(this.basePath, request)
  }

  /**
   * Update an existing privacy subject
   */
  async update(id: string, request: UpdatePrivacyRequest): Promise<PrivacySubject> {
    const response = await apiClient.patch<GetPrivacyResponse>(`${this.basePath}/${id}`, request)
    return response.subject
  }

  /**
   * Delete a privacy subject
   */
  async delete(id: string): Promise<DeletePrivacyResponse> {
    return apiClient.delete<DeletePrivacyResponse>(`${this.basePath}/${id}`)
  }

  /**
   * Get privacy statistics
   */
  async getStats(): Promise<PrivacyStatsResponse> {
    return apiClient.get<PrivacyStatsResponse>(`${this.basePath}/stats`)
  }

  /**
   * Import privacy subjects from CSV file
   */
  async import(file: File): Promise<ImportPrivacyResponse> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.upload<ImportPrivacyResponse>(`${this.basePath}/import`, formData)
  }

  /**
   * Get audit log for a privacy subject
   */
  async getAudit(id: string): Promise<ListAuditResponse> {
    return apiClient.get<ListAuditResponse>(`${this.basePath}/${id}/audit`)
  }
}

export const privacyService = new PrivacyService()
